import type { LensDataset } from '~/types/leLens';

import { declaredDatasetLabel, humaniseFilterLabel } from './filterLabels';

/**
 * F9 (01_USER_FLOWS.md): "Add filter lists dimension columns across all datasets on the board,
 * by name, with the datasets each appears in." A column is pooled once per board, case-insensitive
 * on the name, and carries every dataset that returns it so the picker can say "in N of M datasets"
 * and the manage-filters list can say which datasets a bound filter does not reach.
 *
 * A dataset with declared `columns` (version 2, dry-run backed) contributes its `kind: 'dimension'`
 * columns; a dataset without them yet (not yet declared, per 02_RIGHT_PANEL_SPEC.md) falls back to
 * the legacy `dimensions` name list, same fallback `columnClassification.ts` uses elsewhere.
 */

export type FilterColumnOption = {
  /** Canonical column name, cased as first declared on the board. */
  name: string;
  /** A declared label if any dataset names one for this column, else a derived reader-facing label. */
  label: string;
  /** Every dataset on the board that returns this column, in board order. */
  datasets: Array<LensDataset>;
};

function dimensionNamesOf(dataset: LensDataset): Array<string> {
  return dataset.columns?.length
    ? dataset.columns.filter((column) => column.kind === 'dimension').map((column) => column.name)
    : (dataset.dimensions ?? []);
}

/** All column names a dataset returns, dimension or not: used to check binding coverage rather
 *  than to populate the picker (a filter already bound to a measure-typed legacy column should
 *  still be able to say which datasets it does and does not reach). */
function allColumnNamesOf(dataset: LensDataset): Array<string> {
  return dataset.columns?.length ? dataset.columns.map((column) => column.name) : (dataset.dimensions ?? []);
}

/** The dimension columns available across every dataset on the board, pooled by name and sorted
 *  by reader-facing label. Picking one binds the filter on every dataset carrying it (bindFilterToCards). */
export function dimensionColumnsAcrossDatasets(datasets: ReadonlyArray<LensDataset>): Array<FilterColumnOption> {
  const byKey = new Map<string, FilterColumnOption>();
  for (const dataset of datasets) {
    for (const name of dimensionNamesOf(dataset)) {
      const key = name.toLowerCase();
      const existing = byKey.get(key);
      if (existing) {
        existing.datasets.push(dataset);
        continue;
      }
      const declaredColumnLabel = dataset.columns?.find((column) => column.name === name)?.label;
      // declaredDatasetLabel (filterLabels.ts, unowned) takes a mutable Array<LensDataset>; a
      // ReadonlyArray in here is spread rather than cast, so a future tightening of that
      // signature still catches a real mismatch instead of being silenced.
      const label = humaniseFilterLabel(name, declaredColumnLabel ?? declaredDatasetLabel(name, [...datasets]));
      byKey.set(key, { name, label, datasets: [dataset] });
    }
  }
  return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/** Whether a dataset returns a column, matched case-insensitively against every column it
 *  declares (not only its dimensions): a filter can be bound to any column name, and this
 *  answers coverage for the chip regardless of the column's kind. */
export function datasetHasColumn(dataset: LensDataset, column: string): boolean {
  const key = column.trim().toLowerCase();
  if (!key) return true;
  return allColumnNamesOf(dataset).some((name) => name.toLowerCase() === key);
}

/** Datasets on the board a bound column does not reach, in board order: the set the "not filtered
 *  by {label}" chip lists (F9). */
export function datasetsMissingColumn(datasets: ReadonlyArray<LensDataset>, column: string): Array<LensDataset> {
  return datasets.filter((dataset) => !datasetHasColumn(dataset, column));
}
